import Loader from '../../common/Loader'
import { Link } from 'react-router'
import { isAssessmentFilledOutByManager, isAssessmentFilledOutByUser } from '../../utils.ts'
import { getAssessmentResponseByUserId } from './utils.ts'
import { getMe } from '../../MeProvider.ts'
import { useEffect, useState } from 'react'
import { IAssessment } from '../../services/nextpeakClient'
import { useGetPaginatedAssessments } from '../../http/hooks.ts'
import { Page } from '../../layout/Page.tsx'
import Card from '../../newComponents/Card/Card.tsx'
import Table, { validateTableProps } from '../../newComponents/Table/index.tsx'
import { TableDataCell, TableHeaderCell } from '../../newComponents/Table/Cell.tsx'
import { Tabs } from '../../newComponents/Tabs/index.tsx'
import Badge from '../../newComponents/Badge/index.tsx'
import { useNextPeakClient } from '../../services/nextpeakClient/clientProvider.tsx'
import dayjs from 'dayjs'
import PaginationWithText from '../../newComponents/Pagination/index.tsx'

export const AssessmentsTable = ({
	assessments,
	currentPage,
	totalItems,
	onPageChange,
	loading = false,
	itemsPerPage = 5,
}: {
	assessments: IAssessment[]
	currentPage: number
	totalItems: number
	onPageChange: (page: number) => void
	loading?: boolean
	itemsPerPage?: number
}) => {
	const me = getMe()

	const getAssessmentFilledStatusLabel = (assessment: IAssessment) => {
		if (isAssessmentFilledOutByUser(assessment)) {
			if (isAssessmentFilledOutByManager(assessment)) {
				return (
					<Badge variant="solid" color="success" size="md">
						Completed
					</Badge>
				)
			}
			return (
				<Badge variant="solid" color="warning" size="md">
					Waiting for manager
				</Badge>
			)
		}
		if (isAssessmentFilledOutByManager(assessment)) {
			return (
				<Badge variant="solid" color="warning" size="md">
					Waiting for employee
				</Badge>
			)
		}
		return (
			<Badge variant="solid" color="info" size="md">
				Pending
			</Badge>
		)
	}

	const tableProps = validateTableProps(
		[
			{
				key: 'name',
				content: <TableHeaderCell>Name</TableHeaderCell>,
			},
			{
				key: 'title',
				content: <TableHeaderCell>Title</TableHeaderCell>,
			},
			{
				key: 'position',
				content: <TableHeaderCell>Position</TableHeaderCell>,
			},
			{
				key: 'createdAt',
				content: <TableHeaderCell>Date</TableHeaderCell>,
			},
			{
				key: 'status',
				content: <TableHeaderCell>Status</TableHeaderCell>,
			},
			{
				key: 'action',
				content: <TableHeaderCell>Action</TableHeaderCell>,
			},
		] as const,
		assessments.map((assessment) => {
			const myResponse = getAssessmentResponseByUserId(assessment, me.id!)

			const getActionLink = () => {
				if (!myResponse || myResponse?.completedAt) {
					return `/assessments/${assessment.id}`
				}

				return `/assessments/${assessment.id}/responses/${myResponse.id}`
			}

			return {
				name: <TableDataCell>{assessment.employee?.name}</TableDataCell>,
				title: <TableDataCell>{assessment.assessmentType!.position!.title}</TableDataCell>,
				position: <TableDataCell>{assessment.assessmentType!.position!.level}</TableDataCell>,
				createdAt: <TableDataCell>{dayjs(assessment.createdAt).format('DD, MMM YYYY')}</TableDataCell>,
				status: <TableDataCell>{getAssessmentFilledStatusLabel(assessment as IAssessment)}</TableDataCell>,
				action: (
					<TableDataCell>
						<Link className="hover:text-primary" to={getActionLink()}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth="1.5"
								stroke="currentColor"
								className="size-6"
							>
								<path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15.91 11.672a.375.375 0 0 1 0 .656l-5.603 3.113a.375.375 0 0 1-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112Z"
								/>
							</svg>
						</Link>
					</TableDataCell>
				),
			}
		})
	)

	return (
		<Card>
			{loading && (
				<div className="flex items-center justify-center py-8">
					<Loader />
				</div>
			)}
			{!loading && <Table {...tableProps} />}
			{totalItems > assessments.length && (
				<div className="flex justify-end">
					<PaginationWithText
						initialPage={currentPage}
						totalPages={Math.ceil(totalItems / itemsPerPage)}
						onPageChange={onPageChange}
						loading={loading}
					/>
				</div>
			)}
		</Card>
	)
}

interface TabbedTablesViewProps {
	employeeAssessments: IAssessment[]
	employeeTotal: number
	managerAssessments: IAssessment[]
	managerTotal: number
	isEmployeeLoading: boolean
	isManagerLoading: boolean
	employeePage: number
	managerPage: number
	onEmployeePageChange: (page: number) => void
	onManagerPageChange: (page: number) => void
	itemsPerPage?: number
}

const TabbedTablesView = ({
	employeeAssessments,
	employeeTotal,
	managerAssessments,
	managerTotal,
	isEmployeeLoading,
	isManagerLoading,
	employeePage,
	managerPage,
	onEmployeePageChange,
	onManagerPageChange,
	itemsPerPage = 5,
}: TabbedTablesViewProps) => {
	const [openTab, setOpenTab] = useState(1)

	useEffect(() => {
		if (!employeeAssessments.length && managerAssessments.length) {
			setOpenTab(2)
		}
	}, [employeeAssessments.length, managerAssessments.length])

	return (
		<div>
			<Tabs
				tabs={[
					{ id: 1, label: `Personal (${employeeTotal})` },
					{ id: 2, label: `Subordinates (${managerTotal})` },
				]}
				activeTabId={openTab}
				onTabChange={(tabId) => setOpenTab(Number(tabId))}
			/>
			<>
				<div className={`${openTab === 1 ? 'block' : 'hidden'}`}>
					<AssessmentsTable
						assessments={employeeAssessments}
						currentPage={employeePage}
						totalItems={employeeTotal}
						onPageChange={onEmployeePageChange}
						loading={isEmployeeLoading}
						itemsPerPage={itemsPerPage}
					/>
				</div>
				<div className={`${openTab === 2 ? 'block' : 'hidden'}`}>
					<AssessmentsTable
						assessments={managerAssessments}
						currentPage={managerPage}
						totalItems={managerTotal}
						onPageChange={onManagerPageChange}
						loading={isManagerLoading}
						itemsPerPage={itemsPerPage}
					/>
				</div>
			</>
		</div>
	)
}

const Assessments = () => {
	const me = getMe()
	const client = useNextPeakClient()
	const [employeePage, setEmployeePage] = useState(1)
	const [managerPage, setManagerPage] = useState(1)

	const { data: employeeData, isLoading: isEmployeeLoading } = useGetPaginatedAssessments(client, {
		employeeUserId: me.id!,
		includes: ['employee', 'assessmentType', 'assessmentType.position', 'assessmentResponses'],
		limit: 5,
		offset: (employeePage - 1) * 5,
	})

	const { data: managerData, isLoading: isManagerLoading } = useGetPaginatedAssessments(client, {
		managerUserId: me.id!,
		includes: ['employee', 'assessmentType', 'assessmentType.position', 'assessmentResponses'],
		limit: 5,
		offset: (managerPage - 1) * 5,
	})

	const isLoading = isEmployeeLoading || isManagerLoading

	return (
		<Page title="Assessments" breadcrumbs="Assessments" description="Assessments">
			<div className="flex flex-col gap-10">
				{isLoading && !employeeData && !managerData && <Loader />}
				{(employeeData || managerData) && (
					<TabbedTablesView
						employeeAssessments={employeeData?.data || []}
						employeeTotal={employeeData?.total || 0}
						managerAssessments={managerData?.data || []}
						managerTotal={managerData?.total || 0}
						isEmployeeLoading={isEmployeeLoading}
						isManagerLoading={isManagerLoading}
						employeePage={employeePage}
						managerPage={managerPage}
						onEmployeePageChange={setEmployeePage}
						onManagerPageChange={setManagerPage}
						itemsPerPage={5}
					/>
				)}
			</div>
		</Page>
	)
}

export default Assessments
